import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const SiteContext = createContext(null);

const hexToHsl = (hex) => {
  if (!hex) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/site/config");
      setSettings(res.data.settings);
      setSections(res.data.sections);
    } catch (e) {
      console.error("Falha ao carregar configuração do site", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    const p = hexToHsl(settings.primary_color);
    const s = hexToHsl(settings.secondary_color);
    if (p) root.style.setProperty("--primary", p);
    if (s) root.style.setProperty("--secondary", s);
    if (settings.site_name) document.title = settings.site_name;
    if (settings.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = settings.favicon_url.startsWith("http")
        ? settings.favicon_url
        : `${process.env.REACT_APP_BACKEND_URL}${settings.favicon_url}`;
    }
  }, [settings]);

  const enabledSections = useMemo(() => sections.filter((s) => s.enabled), [sections]);
  const isEnabled = useCallback(
    (key) => sections.find((s) => s.key === key)?.enabled ?? true,
    [sections]
  );

  const value = useMemo(
    () => ({ settings, sections, enabledSections, isEnabled, loading, reload: load }),
    [settings, sections, enabledSections, isEnabled, loading, load]
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);
